import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react"
import { createPortal } from "react-dom"
import { ChevronDown, Search, X } from "lucide-react"

const MAX_DROPDOWN_ITEMS = 50

// Remembers which filter dropdown the user last had open. Selecting a
// path-defining filter (brand/model/category/subcategory) navigates to a new
// page and remounts the whole sidebar, which would otherwise drop the open
// dropdown. Persisting the last-open filter's `name` lets it reopen itself
// after the remount so the user can keep refining where they left off.
const LAST_OPEN_STORAGE_KEY = "fal:filters:last-open"

const readLastOpen = () => {
    try {
        return window.localStorage.getItem(LAST_OPEN_STORAGE_KEY)
    } catch {
        return null
    }
}

const writeLastOpen = (name) => {
    try {
        window.localStorage.setItem(LAST_OPEN_STORAGE_KEY, name)
    } catch {
        // ignore (storage unavailable / private mode)
    }
}

const clearLastOpen = (name) => {
    try {
        if (window.localStorage.getItem(LAST_OPEN_STORAGE_KEY) === name) {
            window.localStorage.removeItem(LAST_OPEN_STORAGE_KEY)
        }
    } catch {
        // ignore (storage unavailable / private mode)
    }
}

export default function MultiSelectChips({
    name,
    label,
    options = [],
    selected = [],
    onChange,
    placeholder = "Search...",
    emptyLabel = "No matches",
    disabled = false,
    disabledHint,
}) {
    const [query, setQuery] = useState("")
    const [open, setOpen] = useState(false)
    const [menuStyle, setMenuStyle] = useState(null)
    const containerRef = useRef(null)
    const inputRef = useRef(null)
    const fieldRef = useRef(null)
    const menuRef = useRef(null)

    const optionMap = useMemo(() => {
        const map = new Map()
        for (const o of options) map.set(o.value, o)
        return map
    }, [options])

    // Remember every label we've ever seen for a value. The server narrows the
    // option list to values still available given the other active filters, so a
    // value the user just selected can drop out of `options` on the next reload.
    // The cache lets its pill keep its proper label instead of disappearing.
    const labelCache = useRef(new Map())
    useEffect(() => {
        for (const o of options) {
            labelCache.current.set(o.value, o.label)
        }
    }, [options])

    // A pill is rendered for every selected value, even one no longer present in
    // `options`, falling back to the cached label (or the raw value) so the user
    // always sees — and can remove — what they picked.
    const selectedOptions = useMemo(
        () =>
            selected.map((value) => ({
                value,
                label:
                    optionMap.get(value)?.label ??
                    labelCache.current.get(value) ??
                    value,
            })),
        [selected, optionMap]
    )

    const matchingOptions = useMemo(() => {
        const term = query.trim().toLowerCase()
        const selectedSet = new Set(selected)
        return options
            .filter((o) => !selectedSet.has(o.value))
            .filter(
                (o) => term.length === 0 || o.label.toLowerCase().includes(term)
            )
    }, [options, selected, query])

    const filteredOptions = useMemo(
        () => matchingOptions.slice(0, MAX_DROPDOWN_ITEMS),
        [matchingOptions]
    )
    const hasMoreOptions = matchingOptions.length > MAX_DROPDOWN_ITEMS

    const openMenu = () => {
        setOpen(true)
        if (name && !disabled) {
            writeLastOpen(name)
        }
    }

    // Reopen on mount if this is the filter the user last had open — restores
    // the dropdown after a filter selection navigates and remounts the sidebar.
    // The sidebar is rendered twice (desktop aside + mobile drawer); only the
    // instance actually visible in this viewport should reopen, otherwise the
    // hidden (display:none) copy portals a bogus 0×0 menu. A zero-width field
    // means this copy is the hidden one, so skip it.
    useEffect(() => {
        if (name && !disabled && readLastOpen() === name) {
            const rect = fieldRef.current?.getBoundingClientRect()
            if (rect && rect.width > 0) {
                setOpen(true)
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    // Only the open menu listens for outside clicks. Dismissing it is an
    // explicit user action, so forget the remembered filter — the reopen is
    // meant to survive a redirect, not a deliberate close.
    useEffect(() => {
        if (!open) {
            return
        }
        const handleClick = (event) => {
            const insideField = containerRef.current?.contains(event.target)
            const insideMenu = menuRef.current?.contains(event.target)
            if (!insideField && !insideMenu) {
                setOpen(false)
                if (name) {
                    clearLastOpen(name)
                }
            }
        }
        document.addEventListener("mousedown", handleClick)
        return () => document.removeEventListener("mousedown", handleClick)
    }, [open, name])

    // The menu is portalled to the body and positioned with fixed coordinates
    // anchored to the input, so it floats above the page instead of expanding
    // the (overflow-auto) sidebar and forcing it to scroll. The drawer opens
    // without animation, so the input is at its final position when the menu
    // mounts — a single measurement suffices; scroll / resize keep it in sync.
    useLayoutEffect(() => {
        if (!open) {
            return
        }

        const reposition = () => {
            const rect = fieldRef.current?.getBoundingClientRect()
            if (!rect) {
                return
            }
            const margin = 8
            const spaceBelow = window.innerHeight - rect.bottom - margin
            const spaceAbove = rect.top - margin
            // Flip the menu above the field when there's little room below it
            // (e.g. the input sits low in the mobile drawer) and more room above,
            // then cap its height to the space actually available so the whole
            // list stays on-screen and scrolls internally instead of running off
            // the bottom edge.
            const openUp = spaceBelow < 200 && spaceAbove > spaceBelow
            const maxHeight = Math.max(
                120,
                Math.min(320, openUp ? spaceAbove : spaceBelow)
            )
            setMenuStyle({
                position: "fixed",
                left: rect.left,
                width: rect.width,
                maxHeight,
                pointerEvents: "auto",
                ...(openUp
                    ? { bottom: window.innerHeight - rect.top + margin }
                    : { top: rect.bottom + margin }),
            })
        }

        reposition()
        window.addEventListener("scroll", reposition, true)
        window.addEventListener("resize", reposition)
        return () => {
            window.removeEventListener("scroll", reposition, true)
            window.removeEventListener("resize", reposition)
        }
    }, [open])

    const add = (value) => {
        if (selected.includes(value)) return
        onChange([...selected, value])
        setQuery("")
        inputRef.current?.focus()
    }

    const remove = (value) => {
        onChange(selected.filter((v) => v !== value))
    }

    const handleKeyDown = (event) => {
        if (event.key === "Backspace" && query === "" && selected.length > 0) {
            event.preventDefault()
            remove(selected[selected.length - 1])
            return
        }
        if (event.key === "Enter" && filteredOptions.length > 0) {
            event.preventDefault()
            add(filteredOptions[0].value)
        }
    }

    return (
        <div ref={containerRef} className="w-full">
            {label && (
                <label className="block mb-2 text-sm font-semibold text-gray-700">
                    {label}
                </label>
            )}

            {selectedOptions.length > 0 && (
                <div className="flex flex-wrap gap-1.5 mb-2">
                    {selectedOptions.map((o) => (
                        <span
                            key={o.value}
                            className="inline-flex items-center gap-1 rounded-full bg-[#7a1820] px-2 py-0.5 text-xs font-medium text-white"
                        >
                            {o.label}
                            <button
                                type="button"
                                aria-label={`Remove ${o.label}`}
                                onClick={() => remove(o.value)}
                                className="rounded-full p-0.5 hover:bg-white/20"
                            >
                                <X className="w-3 h-3" />
                            </button>
                        </span>
                    ))}
                </div>
            )}

            <div ref={fieldRef} className="relative">
                <Search className="pointer-events-none absolute top-1/2 left-2.5 h-4 w-4 -translate-y-1/2 text-gray-500" />
                <input
                    ref={inputRef}
                    type="text"
                    value={query}
                    disabled={disabled}
                    onChange={(event) => {
                        setQuery(event.target.value)
                        openMenu()
                    }}
                    onFocus={() => !disabled && openMenu()}
                    onKeyDown={handleKeyDown}
                    placeholder={
                        disabled ? disabledHint || placeholder : placeholder
                    }
                    className="w-full rounded-md border border-gray-200 bg-white py-1.5 pl-8 pr-8 text-base md:text-sm text-gray-900 placeholder:text-gray-400 focus:border-gray-300 focus:ring-2 focus:ring-gray-100 focus:outline-none disabled:bg-gray-50 disabled:text-gray-400 disabled:cursor-not-allowed"
                />
                <ChevronDown
                    className={`pointer-events-none absolute top-1/2 right-2 h-4 w-4 -translate-y-1/2 text-gray-500 transition-transform ${
                        open ? "rotate-180" : ""
                    }`}
                />

                {open &&
                    !disabled &&
                    menuStyle &&
                    createPortal(
                        <div
                            ref={menuRef}
                            style={menuStyle}
                            className="z-[110] flex flex-col overflow-hidden bg-white border border-gray-200 rounded-md shadow-lg"
                        >
                            {filteredOptions.length === 0 ? (
                                <div className="px-3 py-2 text-sm text-gray-500">
                                    {emptyLabel}
                                </div>
                            ) : (
                                <>
                                    <ul className="flex-1 min-h-0 py-1 overflow-y-auto overscroll-contain">
                                        {filteredOptions.map((o) => (
                                            <li key={o.value}>
                                                <button
                                                    type="button"
                                                    onClick={() => add(o.value)}
                                                    className="w-full text-left px-3 py-1.5 text-sm hover:bg-gray-50"
                                                >
                                                    {o.label}
                                                </button>
                                            </li>
                                        ))}
                                    </ul>
                                    {hasMoreOptions && (
                                        <div className="px-3 py-2 text-xs text-gray-500 border-t border-gray-200 shrink-0">
                                            Use search to discover more options
                                        </div>
                                    )}
                                </>
                            )}
                        </div>,
                        document.body
                    )}
            </div>
        </div>
    )
}
